
/*
 * `Subnavigation` - The subnavigation is used to help the users access lower-level categories in the site&#39;s information architecture.
 *  Subnavigation should only be used whenever we have a lot of information in a certain section/page
 *  of the website that needs to be organised in smaller sections. .
 */
import { SubnavigationComponent, SubnavigationCTA } from './Subnavigation';
import { unlockWindowScroll, lockWindowScroll } from '@owc-utils/common/LockWindow';

import SubnavigationArrowButton from './SubnavigationArrowButton.vue';
import SubnavigationArrows from './SubnavigationArrows.vue';
import { NavigationOption } from '../src/Subnavigation.enums';
import { MediaQueryService } from '@owc-utils/MediaQueryService';
import { MediaQuery } from '@owc-utils/MediaQueryService/MediaQuery';
import { Breakpoints, desktopBreakpoints } from '@owc-utils/MediaQueryService';
import {
  STAGE_TAG_NAME_LIST,
  SUBNAVIGATION_DEBOUNCE_TIME,
  SUBNAVIGATION_SCROLL_THROTTLE_LIMIT,
  SUBNAVIGATION_STAGE_COVERAGE_BEFORE_LEAVE,
  MOBILE_BREAKPOINTS_SUBNAVIGATION,
} from './Subnavigation.config';
import { ButtonType } from '@owc-types/Button.enum';
import { createEventListener } from '@owc-utils/Mixins/EventListenerMixin';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import Personalisation from '@owc-utils/Mixins/Personalisation';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';

let waitForStage: ReturnType<typeof setInterval> | null = null;

export default (ComponentInitializeDispatcher as SubnavigationComponent).extend({
  name: 'owc-subnavigation',
  components: {
    SubnavigationArrowButton,
    SubnavigationArrows,
    SlotContainer,
    ValidationBoxWrapper,
  },
  mixins: [
    createEventListener('scroll', 'scrollHandler', { passive: true }),
    createEventListener('resize', 'resizeEventHandler', {
      debounceTime: SUBNAVIGATION_DEBOUNCE_TIME,
    }),
    OwcDataComponent,
    Personalisation,
  ],
  data() {
    return {
      isSecondLevelContainerScrolled: false,
      isMobile: false,
      isDesktop: false,
      isMQ3: false,
      isSecondLevelOpen: false,
      mediaQueryService: new MediaQueryService(window),
      isConfigurationButtonVisible: false,
      isTopbarVisible: false,
      isStickingOnTop: false,
      isCtaButtonsTransitioned: false,
      subnavigationOffsetTop: 0,
      scrollHandler: null,
      resizeHandler: null,
      stageHeight: 0,
      isArrowMenuVisible: false,
    };
  },
  computed: {
    isSubnavigationTopbarVisible: function() {
      return this.isMobile || this.isTopbarVisible;
    },
    displayLabel: function() {
      return this.personaliseableData?.displayLabel || '';
    },
    visibilityOption() {
      return this.componentData?.visibilitySettings;
    },
    shouldShowToggleB2BB2C() {
      return Boolean(this.visibilityOption?.toggleB2BB2CEnabled);
    },
    navigationItems: function() {
      const navigationItems = this.personaliseableData?.navigationItems;

      if (!navigationItems) {
        return [];
      }

      if (this.isMobile) {
        return navigationItems.filter(
          item =>
            item.navOption == NavigationOption.showAlways || item.navOption == NavigationOption.hideOnDesktop,
        );
      } else {
        return navigationItems.filter(
          item =>
            item.navOption == NavigationOption.showAlways || item.navOption == NavigationOption.hideOnMobile,
        );
      }
    },
    activeNavigationItem: function() {
      return this.navigationItems.find(navigationItem => navigationItem.isActive);
    },
    ctaItems: function() {
      return this.personaliseableData?.ctaItems || [];
    },
    ctaPrimary: function() {
      return this.getItemFromType(this.ctaItems, ButtonType.Primary);
    },
    ctaSecondary: function() {
      return this.getItemFromType(this.ctaItems, ButtonType.Secondary);
    },
    subnavigationAriaLabel: function() {
      return this.personaliseableData?.description || '';
    },
  },
  created() {
    this.scrollHandler = this.createDynamicallyThrottledListener();
  },
  mounted() {
    window.requestAnimationFrame(() => {
      this.resizeEventHandler();
      this.mediaQueryService.observe(true);

      this.scrollEventHandler();
    });
  },
  methods: {
    transitionEnded() {
      this.isCtaButtonsTransitioned = this.isConfigurationButtonVisible;
    },
    updateDeviceViewportFlags: function(mq: MediaQuery) {
      this.isMobile = MOBILE_BREAKPOINTS_SUBNAVIGATION.includes(mq);
      this.isDesktop = desktopBreakpoints.includes(mq);
      this.isMQ3 = Breakpoints.mq3 === mq;
    },
    resizeEventHandler() {
      const currentMediaQuery = this.mediaQueryService.getCurrentMediaQuery();
      this.updateDeviceViewportFlags(currentMediaQuery);

      const stageElement = this.queryStageElement();

      if (stageElement) {
        this.stageHeight = stageElement.clientHeight;
        /**
         * If the stage element exists but the height is still zero, this means
         * that the stage has not yet loaded and we are in for a ride on the
         * CLS train. Unfortunately we'll need to wait for it to be fully loaded
         * so that the subnavigation 'button shortcut behavior' works correctly.
         *
         * If after 10 seconds the stage hasn't been shown yet we just give up, something is clearly
         * wrong or internet is really slow, and a shortcut feature should not be taking up
         * useful resources
         */
        if (this.stageHeight === 0 && !waitForStage) {
          // Max iteration time of 10 seconds
          let waitForStageIterations = 20;

          waitForStage = setInterval(() => {
            this.stageHeight = stageElement.clientHeight;

            if (waitForStage && this.stageHeight !== 0 && waitForStageIterations > 0) {
              clearInterval(waitForStage);
            } else {
              --waitForStageIterations;
            }
          }, 500);
        }
      }
    },
    /*
     * To prevent the subnavigation from jumping when the user scrolls
     * the idea is to have a region with lower throttling limit.
     */
    createDynamicallyThrottledListener() {
      let dynamicThottleLimit = 1;
      let ableToExecute = true;
      return () => {
        if (!ableToExecute) {
          return;
        }
        this.scrollEventHandler();

        // To ensure that the subnavigation is placed in the middle
        // of that super sensible region the top offset to multiplied by 2
        dynamicThottleLimit =
          this.subnavigationOffsetTop * 2 < window.scrollY ? SUBNAVIGATION_SCROLL_THROTTLE_LIMIT : 1;
        // reset the execution flag and set timeout with new throttle limit
        ableToExecute = false;
        setTimeout(() => (ableToExecute = true), dynamicThottleLimit);
      };
    },
    scrollEventHandler() {
      // Moved the check into the scroll function to prevent the
      // subnavigation jumping into the header. When header pops in later then the subnavigation
      // and the offsetTop is not updated then the subnaavigation jumps right into
      // the header.
      const offsetTop = (this.$el as HTMLElement).offsetTop;
      if (offsetTop > 0 && this.subnavigationOffsetTop !== offsetTop) {
        this.subnavigationOffsetTop = offsetTop;
      }

      const scrollY = window.scrollY;
      this.isStickingOnTop = scrollY > 0 && scrollY >= this.subnavigationOffsetTop;

      if (this.stageHeight > 0) {
        const visibleStageHeight = this.stageHeight * SUBNAVIGATION_STAGE_COVERAGE_BEFORE_LEAVE;
        if (scrollY > visibleStageHeight) {
          this.leavingStage();
        } else {
          this.enteringStage();
        }
      }
    },
    queryStageElement() {
      const stageElements = STAGE_TAG_NAME_LIST.join(',');

      return document.querySelector(`${stageElements}`);
    },
    toggleArrowMenu() {
      this.isArrowMenuVisible = !this.isArrowMenuVisible;
    },
    leavingStage() {
      this.isTopbarVisible = true;
      this.isConfigurationButtonVisible = true;
    },
    enteringStage() {
      this.isTopbarVisible = false;
      this.isConfigurationButtonVisible = false;
    },
    getItemFromType(itemArray: SubnavigationCTA[], type: ButtonType) {
      return itemArray.find(item => item.link.type === type);
    },
    openSecondLevel: function() {
      this.isSecondLevelOpen = true;
    },
    closeSecondLevel: function() {
      this.isSecondLevelOpen = false;
    },
    handleSecondLevelScroll: function() {
      const secondLevelContainer = this.$refs.secondLevelContainer as HTMLElement;
      const amountScrolled = secondLevelContainer.scrollTop;
      this.isSecondLevelContainerScrolled = amountScrolled > 0;
    },
    unlockWindowScroll,
    lockWindowScroll,
  },
});
